import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, differenceInDays } from 'date-fns';
import LeaseRiskChart from '../components/LeaseRiskChart';
import RenewalAutomationSettings from '../components/RenewalAutomationSettings';
import RenewalPipeline from '../components/RenewalPipeline';

const { FiAlertCircle, FiCheckCircle, FiClock, FiMail, FiTrendingDown, FiArrowRight, FiShield, FiZap, FiStar, FiFilter, FiSettings, FiLayers } = FiIcons;

const LeaseExpirations = () => {
  const { data, updateData, logAccess, addRecord } = useStore();
  const [timeframe, setTimeframe] = useState(90);
  const [showSettings, setShowSettings] = useState(false);
  const [activeView, setActiveView] = useState('list'); // 'list', 'pipeline'
  
  const today = new Date();

  const expirations = useMemo(() => {
    return data.properties
      .map(p => {
        const lease = data.leases.find(l => l.id === p.currentLeaseId);
        if (!lease || !lease.endDate) return null;
        
        const daysLeft = differenceInDays(parseISO(lease.endDate), today);
        const feedback = data.feedback.filter(f => f.tenantId === lease.tenantId);
        const avgSentiment = feedback.length > 0 
          ? feedback.filter(f => f.sentiment === 'Positive').length / feedback.length 
          : 0.8;

        const riskScore = daysLeft < 0 ? 100 : Math.max(0, 100 - (daysLeft * 1.5) - (avgSentiment * 20));
        
        return {
          ...p,
          lease,
          daysLeft,
          riskScore: Math.round(riskScore),
          potentialLoss: Math.round((p.rentRate / 30) * 15)
        };
      })
      .filter(item => item !== null)
      .filter(item => item.daysLeft <= timeframe)
      .sort((a, b) => b.riskScore - a.riskScore);
  }, [data, timeframe]);

  const stats = {
    totalRiskValue: expirations.reduce((sum, e) => sum + e.potentialLoss, 0),
    avgChurnRisk: Math.round(expirations.reduce((sum, e) => sum + e.riskScore, 0) / (expirations.length || 1)),
    pendingRenewals: expirations.filter(e => e.riskScore > 70).length
  };

  const handleProcessAutomations = () => {
    const threshold = data.renewalSettings?.noticeDaysBefore || 60;
    const toNotify = expirations.filter(e => e.daysLeft <= threshold && !e.noticeSent);
    
    toNotify.forEach(e => {
      addRecord('communications', {
        tenantId: e.lease.tenantId,
        propertyId: e.id,
        type: 'Email',
        date: format(new Date(), 'yyyy-MM-dd'),
        subject: 'Lease Renewal Inquiry',
        notes: `Automated renewal notice sent for ${e.name}.`,
        status: 'Sent'
      });
      // In a real app, we'd update the property or lease record here
    });

    logAccess(`Processed ${toNotify.length} Automated Renewal Notices`, 'System', 'Success');
    alert(`Successfully processed ${toNotify.length} automated notices.`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Lease Intelligence</h2>
          <p className="text-sm text-gray-500 font-medium italic">Predictive churn modeling and vacancy mitigation.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center px-5 py-3 rounded-2xl transition-all font-black text-xs uppercase tracking-wider ${showSettings ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white border border-gray-100 text-gray-600 hover:bg-gray-50'}`}
          >
            <SafeIcon icon={FiSettings} className="mr-2" /> Rules
          </button>
          <button 
            onClick={handleProcessAutomations}
            className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-xs uppercase tracking-wider"
          >
            <SafeIcon icon={FiZap} className="mr-2" /> Run Automations
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm mb-6 flex items-center justify-between">
            <div className="flex bg-gray-50 p-1 rounded-2xl">
              <button 
                onClick={() => setActiveView('list')}
                className={`flex items-center px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeView === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400'}`}
              >
                <SafeIcon icon={FiLayers} className="mr-2" /> Risk Audit
              </button>
              <button 
                onClick={() => setActiveView('pipeline')}
                className={`flex items-center px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeView === 'pipeline' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400'}`}
              >
                <SafeIcon icon={FiTrendingDown} className="mr-2" /> Pipeline
              </button>
            </div>
            <div className="flex bg-gray-50 p-1 rounded-2xl">
              {[30, 60, 90, 120].map(d => (
                <button
                  key={d}
                  onClick={() => setTimeframe(d)}
                  className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-xl transition-all ${timeframe === d ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {d} Days
                </button>
              ))}
            </div>
          </div>
          
          {activeView === 'list' ? (
            <LeaseRiskChart properties={data.properties} leases={data.leases} />
          ) : (
            <RenewalPipeline items={expirations} />
          )}
        </div>

        <div className="space-y-6">
          {showSettings ? (
            <RenewalAutomationSettings 
              settings={data.renewalSettings} 
              templates={data.communicationTemplates}
              onSave={(s) => {
                updateData('renewalSettings', s);
                setShowSettings(false);
              }} 
            />
          ) : (
            <>
              <div className="bg-indigo-600 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden group">
                <h3 className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Portfolio Churn Risk</h3>
                <p className="text-5xl font-black">{stats.avgChurnRisk}%</p>
                <p className="text-[10px] font-bold uppercase mt-2 text-indigo-100">Across {expirations.length} units in view</p>
                <SafeIcon icon={FiTrendingDown} className="absolute -bottom-4 -right-4 text-white/10 group-hover:scale-110 transition-transform" size={100} />
              </div>

              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Urgent Vacancy Risk</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500 text-white rounded-xl flex items-center justify-center font-black text-sm shadow-md shadow-red-100">!</div>
                      <div>
                        <p className="text-xs font-black text-red-700 uppercase">{stats.pendingRenewals} Units Overdue</p>
                        <p className="text-[8px] font-bold text-red-400 uppercase">Immediate Action Required</p>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-red-400" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-2xl border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500 text-white rounded-xl flex items-center justify-center font-black text-sm shadow-md shadow-amber-100">?</div>
                      <div>
                        <p className="text-xs font-black text-amber-700 uppercase">${stats.totalRiskValue.toLocaleString()} Risk</p>
                        <p className="text-[8px] font-bold text-amber-400 uppercase">Estimated Revenue Gap</p>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-amber-400" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiClock} className="text-indigo-600" /> Predictive Expiration List
          </h3>
          <div className="flex items-center gap-4 text-[9px] font-black uppercase text-gray-400">
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> Churn Risk &gt; 70%</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-indigo-500" /> Healthy Stay</span>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {expirations.map((item) => (
            <div key={item.id} className="p-6 hover:bg-gray-50/50 transition-all group">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex items-center gap-4 lg:w-1/4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${item.riskScore > 70 ? 'bg-red-500' : 'bg-indigo-500'}`}>
                    <SafeIcon icon={item.riskScore > 70 ? FiAlertCircle : FiCheckCircle} size={20} />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 leading-tight">{item.name}</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{item.lease.tenantName}</p>
                  </div>
                </div>
                <div className="flex-1 px-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${item.riskScore > 70 ? 'text-red-500' : 'text-gray-400'}`}>
                      Churn Probability: {item.riskScore}%
                    </span>
                    <span className="text-[10px] font-black text-gray-900 uppercase">
                      Ends {item.lease.endDate}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-1000 ${item.riskScore > 70 ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${item.riskScore}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-6 lg:w-1/4 justify-end">
                  <div className="text-right">
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Stay Health</p>
                    <div className="flex items-center gap-1 text-yellow-400">
                      <SafeIcon icon={FiStar} className="fill-current" size={12} />
                      <span className="text-xs font-black text-gray-900">High</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => window.location.hash = '#/communications'} className="p-3 bg-white border border-gray-200 text-gray-400 rounded-2xl hover:text-indigo-600 hover:border-indigo-200 transition shadow-sm" title="Send Renewal Blueprint">
                      <SafeIcon icon={FiZap} size={18} />
                    </button>
                    <button className="px-4 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center gap-2">
                      Renew <SafeIcon icon={FiArrowRight} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {expirations.length === 0 && (
            <div className="py-24 text-center">
              <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheckCircle} size={40} />
              </div>
              <h4 className="text-xl font-black text-gray-900">Portfolio is Stable</h4>
              <p className="text-sm text-gray-500 max-w-xs mx-auto mt-2">No critical expirations in the next {timeframe} days.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaseExpirations;